/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { ResponsiveBannerImage } from '@/components/responsive-banner-image';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

/** Ribbon background per brand token. Keys match the `ribbonColor` enum values. */
const RIBBON_COLOR_CLASS = {
    'primary-static': 'bg-ps-primary-static',
    'primary-interactive': 'bg-ps-primary-interactive',
    accent: 'bg-ps-accent',
    black: 'bg-ps-black',
} as const;

type RibbonColor = keyof typeof RIBBON_COLOR_CLASS;

const DEFAULT_RIBBON_COLOR: RibbonColor = 'primary-static';

/** The Carousel sets how many cards are visible; these cover its common setups (e.g. 3 / 4 / 6.5). */
const CATALOG_CARD_WIDTHS = { base: '50vw', md: '25vw', lg: '16vw' };

@Component('catalogCard', {
    name: 'Catalog Card',
    group: 'priceshoes_content',
    description:
        'Card for the Carousel, e.g. "Catálogos digitales": cover image, optional ribbon and title. The whole card links to the configured URL.',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class CatalogCardMetadata {
    @AttributeDefinition({ name: 'Image', type: 'image', required: true, description: 'Cover image (desktop)' })
    image!: string;

    @AttributeDefinition({
        name: 'Mobile Image',
        type: 'image',
        required: false,
        description: 'Cover image for mobile. Falls back to the desktop image when empty.',
    })
    mobileImage?: string;

    @AttributeDefinition({ name: 'Image Alt Text', type: 'string', required: false })
    imageAlt?: string;

    @AttributeDefinition({
        id: 'ribbon',
        name: 'Ribbon',
        description: 'Short label shown on top of the card, e.g. "Nuevo". Leave empty to hide it.',
        type: 'string',
        required: false,
    })
    ribbon?: string;

    @AttributeDefinition({
        id: 'ribbonColor',
        name: 'Ribbon Color',
        description: 'Brand color of the ribbon background. The text is always white.',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically. Keep in sync with RIBBON_COLOR_CLASS.
        values: ['primary-static', 'primary-interactive', 'accent', 'black'],
        defaultValue: 'primary-static',
    })
    ribbonColor?: string;

    @AttributeDefinition({ name: 'Title', type: 'string', required: false, description: 'e.g. catalog name' })
    title?: string;

    @AttributeDefinition({ name: 'Link', type: 'url', required: true, description: 'Where the card redirects' })
    link!: string;
}

interface CatalogCardProps {
    image?: Image;
    mobileImage?: Image;
    imageAlt?: string;
    ribbon?: string;
    ribbonColor?: string;
    title?: string;
    link?: string;
    className?: string;
}

function isRibbonColor(value: string | undefined): value is RibbonColor {
    return Boolean(value && value in RIBBON_COLOR_CLASS);
}

/**
 * "Catálogos digitales" card (PRIC-40): cover image with an optional ribbon (top left on desktop,
 * top right on mobile, as in Figma) and title over a dark bottom gradient. The whole card is a link.
 * Width comes from the Carousel; the card keeps the 190×250 Figma ratio.
 */
export default function CatalogCard({
    image,
    mobileImage,
    imageAlt,
    ribbon,
    ribbonColor,
    title,
    link,
    className,
}: CatalogCardProps): ReactElement | null {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();

    const hasImage = Boolean(image?.url || mobileImage?.url);
    if (!hasImage && !isDesignMode) return null;

    const ribbonText = ribbon?.trim();
    const ribbonClass = RIBBON_COLOR_CLASS[isRibbonColor(ribbonColor) ? ribbonColor : DEFAULT_RIBBON_COLOR];
    const heading = title?.trim();

    const cardClass = cn(
        'relative flex aspect-[190/250] w-full flex-col justify-end overflow-hidden rounded-2xl bg-ps-gray-200 p-4',
        className
    );

    const content = (
        <>
            {hasImage ? (
                <>
                    <ResponsiveBannerImage
                        image={image}
                        mobileImage={mobileImage}
                        alt={imageAlt}
                        widths={CATALOG_CARD_WIDTHS}
                        className="absolute inset-0 size-full"
                    />
                    {heading && (
                        // Keeps the white text readable on any cover.
                        <div
                            aria-hidden="true"
                            className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ps-black/60 to-transparent"
                        />
                    )}
                </>
            ) : (
                <span className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-ps-gray-500">
                    {t('catalogCard.emptyLabel')}
                </span>
            )}

            {ribbonText && (
                <span
                    data-slot="catalog-card-ribbon"
                    className={cn(
                        'absolute right-3 top-3 flex h-[18px] max-w-[120px] items-center overflow-hidden rounded px-2 text-[10px] font-bold uppercase leading-4 text-ps-white md:left-4 md:right-auto md:top-4',
                        ribbonClass
                    )}>
                    {ribbonText}
                </span>
            )}

            {heading && <h3 className="relative text-base font-bold leading-6 text-ps-white">{heading}</h3>}
        </>
    );

    return link ? (
        <Link
            to={link}
            data-slot="catalog-card"
            aria-label={heading ? undefined : imageAlt || undefined}
            className={cn(
                cardClass,
                'transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ps-focus'
            )}>
            {content}
        </Link>
    ) : (
        <div data-slot="catalog-card" className={cardClass}>
            {content}
        </div>
    );
}
